import React, { FC } from 'react';
import { pick } from 'lodash';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import {
  GroupStatus,
  UpdateGroupMutation,
  UpdateGroupInput,
  useUpdateGroupMutation,
  useGetGroupQuery,
} from 'api';
import { Layouts, Loaders } from 'components';
import * as paths from 'paths';
import { groups } from 'paths';
import { useNotify, useCapabilities } from 'hooks';
import { useEditGroup } from 'features/groups/updaters';

import { GroupDataProvider, GroupModeratorsProvider, GroupSourcesProvider } from '../providers';
import { GroupSchema } from '../validations';
import { GroupForm } from '../components/Edit';

const EditGroup: FC = () => {
  const { t } = useTranslation();
  const { needs } = useCapabilities();
  const { group_id } = useParams<{ group_id: string }>();
  const notify = useNotify();
  const navigate = useNavigate();

  const hasPermissions = needs('Access.Moderator', group_id);

  const editMutationOptions = useEditGroup();
  const { mutate, isLoading: isMutationLoading } = useUpdateGroupMutation({
    onSettled: handleCompleted,
    ...editMutationOptions,
  });

  const { data, isLoading: isQueryLoading } = useGetGroupQuery(
    { id: group_id },
    {
      onError: notify.queryError,
    }
  );

  function getInitialValues() {
    const { getGroup } = data;

    return pick(
      getGroup,
      'id',
      'title',
      'color',
      'icon_color',
      'icon',
      'image',
      'group_status',
      'content_approval_type',
      'allow_user_posts',
      'read_only',
      'department_id',
      'location_id',
      'is_private'
    );
  }

  function onSubmit(values: UpdateGroupInput) {
    mutate({ data: { ...values, id: group_id } });
  }

  function handleCompleted({ updateGroup }: UpdateGroupMutation) {
    const isDeleted = updateGroup.group_status === GroupStatus.Deleted;

    if (!isDeleted) {
      notify.info({ message: t('groups:updateSuccess', { group: updateGroup.title }) });
      navigate(`/group/${group_id}`);
      return;
    }

    notify.info({ message: t('groups:deleteGroupNotify') });
    navigate(groups);
  }

  function renderMain() {
    if (isQueryLoading) {
      return <Loaders.Standard />;
    }

    return (
      <GroupDataProvider>
        <GroupModeratorsProvider>
          <GroupSourcesProvider>
            <Formik
              onSubmit={onSubmit}
              initialValues={getInitialValues()}
              validationSchema={GroupSchema}
            >
              <GroupForm action="edit" isWorking={isMutationLoading} />
            </Formik>
          </GroupSourcesProvider>
        </GroupModeratorsProvider>
      </GroupDataProvider>
    );
  }

  if (!hasPermissions) {
    return <Navigate to={paths.groups} replace />;
  }

  return <Layouts.App1Column navigation desktopSize={12} main={renderMain()} gutterBottom />;
};

export default EditGroup;
